// Configuración para el sistema de infinite scroll optimizado
// Sincronizado con el backend implementado

const INFINITE_SCROLL_CONFIG = {
  // Paginación
  ITEMS_PER_PAGE: 18, // Sincronizado con el backend
  
  // Timeouts y delays
  DEBOUNCE_DELAY: 800, // ms - Para evitar rate limiting (30 req/min)
  REQUEST_TIMEOUT: 15000, // ms - Timeout para requests
  ERROR_AUTO_DISMISS: 3000, // ms - Auto-dismiss de errores
  
  // Rate limiting (información para el usuario)
  RATE_LIMIT_INFO: {
    ventas: '30 consultas por minuto',
    admin: '100 consultas por 5 minutos',
  },
  
  // Validaciones
  MAX_DATE_RANGE_DAYS: 365, // Máximo rango de fechas permitido
  
  // Intersection Observer
  OBSERVER_CONFIG: {
    threshold: 0.1,
    rootMargin: '100px', // Cargar antes de llegar al final
  },
  
  // Loading states
  SKELETON_COUNT: 9, // Número de skeleton loaders (múltiplo de 3 para grid)
  MAX_ANIMATION_DELAY: 0.3, // Máximo delay para animaciones
  ANIMATION_DELAY_INCREMENT: 0.05, // Incremento del delay por item
  
  // Error types
  ERROR_TYPES: {
    RATE_LIMIT: 'RATE_LIMIT',
    VALIDATION: 'VALIDATION',
    SERVER: 'SERVER',
    NETWORK: 'NETWORK',
    GENERIC: 'GENERIC',
  },
  
  // Status codes que el backend puede retornar
  STATUS_CODES: {
    RATE_LIMITED: 429,
    BAD_REQUEST: 400,
    INTERNAL_SERVER_ERROR: 500,
  }
};

// Funciones de utilidad para el infinite scroll
export const infiniteScrollUtils = {
  // Determinar el tipo de error basado en la respuesta
  getErrorType: (error) => {
    if (error.response?.status === INFINITE_SCROLL_CONFIG.STATUS_CODES.RATE_LIMITED) {
      return INFINITE_SCROLL_CONFIG.ERROR_TYPES.RATE_LIMIT;
    }
    if (error.response?.status === INFINITE_SCROLL_CONFIG.STATUS_CODES.BAD_REQUEST) {
      return INFINITE_SCROLL_CONFIG.ERROR_TYPES.VALIDATION;
    }
    if (error.response?.status >= INFINITE_SCROLL_CONFIG.STATUS_CODES.INTERNAL_SERVER_ERROR) {
      return INFINITE_SCROLL_CONFIG.ERROR_TYPES.SERVER;
    }
    if (!error.response) {
      return INFINITE_SCROLL_CONFIG.ERROR_TYPES.NETWORK;
    }
    return INFINITE_SCROLL_CONFIG.ERROR_TYPES.GENERIC;
  },
  
  // Calcular diferencia en días entre fechas
  calculateDateDifference: (startDate, endDate) => {
    return (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
  },
  
  // Validar rango de fechas
  validateDateRange: (startDate, endDate) => {
    if (!startDate || !endDate) return { valid: false, error: 'Ambas fechas son requeridas' };
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start > end) {
      return { valid: false, error: 'La fecha de inicio no puede ser mayor que la fecha de fin' };
    }
    
    const daysDiff = infiniteScrollUtils.calculateDateDifference(startDate, endDate);
    if (daysDiff > INFINITE_SCROLL_CONFIG.MAX_DATE_RANGE_DAYS) {
      return { valid: false, error: 'El rango de fechas no puede ser mayor a 1 año' };
    }
    
    return { valid: true, daysDiff };
  },
  
  // Formatear fecha para display
  formatDate: (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('es-AR');
    } catch {
      return dateString;
    }
  },
  
  // Construir URL de API con parámetros
  buildApiUrl: (baseUrl, page, fechaInicio = '', fechaFin = '') => {
    let url = `${baseUrl}/ventas/admin?page=${page}&limit=${INFINITE_SCROLL_CONFIG.ITEMS_PER_PAGE}`;
    
    if (fechaInicio && fechaFin) {
      url += `&fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`;
    }
    
    return url;
  },
  
  // Log de performance para desarrollo
  logPerformance: (responseData, page) => {
    if (process.env.NODE_ENV === 'development') {
      const { 
        ventas, 
        currentPage, 
        totalCount, 
        executionTime, 
        cached 
      } = responseData;
      
      console.group(`📊 Infinite Scroll Performance - Página ${page}`);
      console.log(`✅ Ventas cargadas: ${ventas?.length || 0}`);
      console.log(`📄 Página actual: ${currentPage}`);
      console.log(`🔢 Total disponible: ${totalCount}`);
      console.log(`⏱️ Tiempo de ejecución: ${executionTime}ms`);
      console.log(`💾 Desde caché: ${cached ? 'Sí' : 'No'}`);
      
      if (executionTime > 1000) {
        console.warn(`🐌 Request lento detectado: ${executionTime}ms`);
      }
      if (executionTime < 100) {
        console.log(`⚡ Request rápido: ${executionTime}ms`);
      }
      
      console.groupEnd();
    }
  }
};

export default INFINITE_SCROLL_CONFIG;
